import { APP_MODE_TYPES, appMode } from 'src/config'

const COMMANDS = {
  STOP_CONSOLE_STREAM: 'stop-stream',
  REQUEST_CONSOLE_DATA: 'request-console-stream',
  SEND_CONSOLE_COMMAND: 'send-console-command',
  CONSOLE_DATA: 'console-data'
}

const LOG_LEVELS = {
  LOG: 'log',
  WARN: 'warn',
  ERROR: 'error'
}

const CHANNEL_NAME = 'RtcChannel'

const SUPPORTED_INDY_APPLICATIONS = [APP_MODE_TYPES.ADMIN, APP_MODE_TYPES.REGISTER, APP_MODE_TYPES.KIOSK]

export default async ({ app }) => {
  let currentChannel = null
  let streamingActive = false

  // Only support admin (consumer), pos (producer), kiosk (producer)
  if (!_.includes(SUPPORTED_INDY_APPLICATIONS, appMode)) return

  if (!app.config.globalProperties.$cable) return

  // Consumer method to start a console session
  // e.g. a super user in admin mode
  async function startConsoleStream (registerClientId, stopCallback, dataCallback) {
    streamingActive = true

    // Setup a channel to receive console data
    currentChannel = app.config.globalProperties.$cable.subscriptions.create({ channel: CHANNEL_NAME, room: registerClientId + '_consumer' }, {
      async received (data) {
        if (data.command === COMMANDS.STOP_CONSOLE_STREAM) {
          // Stop the stream if requested, return a message to let the consumer know why
          stopCallback(data.message)
          currentChannel.unsubscribe()
        } else {
          // Send console data to the consumer
          dataCallback(data.data)
        }
      }
    })

    // Request the producer to start sending console data
    currentChannel.send({
      command: COMMANDS.REQUEST_CONSOLE_DATA,
      client_id: registerClientId,
      data: {}
    })
  }

  function setupConsoleStream (registerClientId) {
    // We are going to override the console.log, console.warn, console.error functions
    // We need to store the original functions so we can restore them when the stream is stopped
    const originalConsoleLog = console.log.bind(console)
    const originalConsoleWarn = console.warn.bind(console)
    const originalConsoleError = console.error.bind(console)

    // Setup a channel to send console data
    currentChannel = app.config.globalProperties.$cable.subscriptions.create(
      {
        channel: CHANNEL_NAME,
        room: registerClientId + '_producer'
      },
      {
        async received (data) {
          if (data.command === COMMANDS.STOP_CONSOLE_STREAM) {
            // Stream has been stopped, restore the original console functions
            // Restore the original console functions
            window.console.log = originalConsoleLog
            window.console.warn = originalConsoleWarn
            window.console.error = originalConsoleError
            stopConsoleStream()
          }

          if (data.command === COMMANDS.SEND_CONSOLE_COMMAND) {
            try {
              // We are going to use eval to execute the requested command
              // We restrict this function to super users only so we can trust the commands
              // eslint-disable-next-line no-eval
              await console.log(eval(data.data.command))
            } catch (e) {
              // This will cause the error to be sent to the consumer since we override the console.error function
              console.error(e.message, e.stack)
            }
          }
        }
      })

    // Mark the stream as active so we send console data
    streamingActive = true

    // Override the console functions to send data to the consumer
    window.console.log = (...args) => {
      originalConsoleLog.apply(null, args)
      sendAndParse(args, LOG_LEVELS.LOG, registerClientId)
    }

    window.console.warn = (...args) => {
      originalConsoleWarn.apply(null, args)
      sendAndParse(args, LOG_LEVELS.WARN, registerClientId)
    }

    window.console.error = (...args) => {
      originalConsoleError.apply(null, args)
      sendAndParse(args, LOG_LEVELS.ERROR, registerClientId)
    }

    // This will notify the consumer the stream is ready
    console.log('Remote console stream setup')
  }

  // Send a console command to the producer to execute
  function sendConsoleCommand (command, registerClientId) {
    if (streamingActive) {
      currentChannel.send({
        command: COMMANDS.SEND_CONSOLE_COMMAND,
        client_id: registerClientId,
        data: {
          command
        }
      })
    }
  }

  function sendAndParse (args, level, registerClientId) {
    // We need to parse certain arguments so they can be displayed correctly
    if (streamingActive) {
      let parsedArgs = args.map(arg => {
        if (arg === undefined) {
          return 'undefined'
        } else if (arg === null) {
          return 'null'
        } else if (typeof arg === 'object') {
          // We need to stringify the object so it can be displayed nicely
          return JSON.stringify(_.extend({}, arg))
        } else {
          return arg
        }
      })

      parsedArgs = parsedArgs.join('\n')

      // Send the parsed data to the consumer
      currentChannel.send({
        command: COMMANDS.CONSOLE_DATA,
        client_id: registerClientId,
        data: {
          message: parsedArgs,
          level,
          timestamp: new Date().toISOString()
        }
      })
    }
  }

  function stopConsoleStream () {
    if (currentChannel && streamingActive) {
      // Unsubscribe from the channel so we are no longer sending/receiving data
      currentChannel.unsubscribe()
    }
    streamingActive = false
  }

  app.config.globalProperties.$startConsoleStream = startConsoleStream
  app.config.globalProperties.$stopConsoleStream = stopConsoleStream
  app.config.globalProperties.$setupConsoleStream = setupConsoleStream
  app.config.globalProperties.$sendConsoleCommand = sendConsoleCommand
}
